<template>
  <div class="flex flex-col gap-2">
    <LabelComponent v-if="!hideLabel">{{ props.label }}</LabelComponent>
    <div class="relative">
      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-grey-light-400 text-lg">
        <Percent v-if="props.icon === 'percentage'" class="h-4 w-4" />
        <Calendar1 v-else-if="props.icon === 'calendar'" class="h-4 w-4" />
      </span>
      <Input type="text" class="pl-8" v-model="inputValue" :disabled="props.disabled" @input="onInput" />
    </div>
    <div v-if="!hideError && error !== ''" class="flex items-center mt-1">
      <span class="text-sm font-medium" :class="isValid ? 'text-warning-400' : 'text-error-500'">
        {{ error }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Input } from '@/components/ui/input';
import { Calendar1, Percent } from 'lucide-vue-next';
import { ref, watch } from 'vue';
import LabelComponent from '../ui/label/LabelComponent.vue';

const props = withDefaults(
  defineProps<{
    modelValue: number;
    icon?: 'percentage' | 'calendar';
    label?: string;
    disabled?: boolean;
    min?: number;
    max?: number;
    warningMin?: number;
    warningMax?: number;
    hideError?: boolean;
    hideLabel?: boolean;
  }>(),
  {
    label: 'Percentage',
    icon: 'percentage',
    min: 0,
    max: 100,
    warningMin: 0,
    warningMax: 100,
    hideError: false,
    hideLabel: false,
  },
);

const emit = defineEmits(['update:modelValue', 'update:isValid']);

const inputValue = ref(props.modelValue.toString());

watch(
  () => props.modelValue,
  (val) => {
    // Sync internal input when modelValue changes externally
    inputValue.value = val.toString();
  },
  { immediate: true },
);

const error = ref('');
const isValid = ref(true);

const onInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;

  // Only allow digits
  value = value.replace(/[^0-9]/g, '');

  // Remove leading zeros (unless it's just "0")
  value = value.replace(/^0+(?=\d)/, '');

  if (value.length > 3) value = value.slice(0, 3);

  const parsed = parseInt(value, 10);

  if (isNaN(parsed)) {
    error.value = 'Please enter a valid percentage';
    isValid.value = false;
  } else if (parsed < props.min) {
    error.value = `Percentage must be at least ${props.min}%`;
    isValid.value = false;
  } else if (parsed > props.max) {
    error.value = `Percentage must not exceed ${props.max}%`;
    isValid.value = false;
  } else if (parsed < props.warningMin) {
    error.value = `Warning: Value is below the recommended minimum of ${props.warningMin}%`;
    isValid.value = true;
  } else if (parsed > props.warningMax) {
    error.value = `Warning: Value exceeds the recommended maximum of ${props.warningMax}%`;
    isValid.value = true;
  } else {
    error.value = '';
    isValid.value = true;
  }

  inputValue.value = isNaN(parsed) ? '' : parsed.toString();

  emit('update:modelValue', isNaN(parsed) ? 0 : parsed);
  emit('update:isValid', isValid.value);
};
</script>
